import { BrowserProvider, parseEther, formatEther } from 'ethers';
import { Connection, PublicKey, Transaction, SystemProgram, LAMPORTS_PER_SOL } from '@solana/web3.js';
import Decimal from 'decimal.js';
import { supabase } from '../lib/supabase';

class TransactionService {
  constructor() {
    this.ethereumProvider = null;
    this.solanaConnection = new Connection('https://api.mainnet-beta.solana.com');
    this.transactionHistory = [];
  }

  // Save transaction to database
  async saveTransaction(userId, walletId, txData) {
    try {
      const { data, error } = await supabase?.from('user_transactions')?.insert({
          user_id: userId,
          wallet_id: walletId,
          transaction_type: txData?.type,
          chain: txData?.chain,
          hash: txData?.hash || null,
          signature: txData?.signature || null,
          from_address: txData?.from,
          to_address: txData?.to,
          asset_symbol: txData?.asset,
          amount: txData?.amount,
          usd_value: txData?.usdValue || null,
          fee: txData?.fee || null,
          fee_currency: txData?.feeCurrency || null,
          status: txData?.status,
          block_number: txData?.blockNumber || null,
          timestamp: txData?.timestamp || new Date()?.toISOString()
        })?.select()?.single();

      if (error) throw error;
      return data;
    } catch (error) {
      console.error('Error saving transaction:', error);
      throw error;
    }
  }

  // Get user transactions from database
  async getUserTransactions(userId, limit = 20) {
    try {
      const { data, error } = await supabase?.from('user_transactions')?.select('*')?.eq('user_id', userId)?.order('timestamp', { ascending: false })?.limit(limit);

      if (error) throw error;
      return data || [];
    } catch (error) {
      console.error('Error fetching user transactions:', error);
      return [];
    }
  }

  // Send Ethereum transaction
  async sendEthereum(fromAddress, toAddress, amount) {
    try {
      if (!window?.ethereum) {
        throw new Error('MetaMask not installed');
      }

      const provider = new BrowserProvider(window.ethereum);
      const signer = await provider?.getSigner();

      const tx = await signer?.sendTransaction({
        to: toAddress,
        value: parseEther(amount?.toString())
      });

      const receipt = await tx?.wait();

      // Save to database
      const { data: { user } } = await supabase?.auth?.getUser();
      if (user) {
        await this.saveTransaction(user?.id, null, {
          type: 'send',
          chain: 'ethereum',
          hash: receipt?.hash,
          from: fromAddress,
          to: toAddress,
          asset: 'ETH',
          amount: amount,
          fee: formatEther(receipt?.gasUsed * receipt?.gasPrice || 0n),
          feeCurrency: 'ETH',
          status: receipt?.status === 1 ? 'confirmed' : 'failed',
          blockNumber: receipt?.blockNumber
        });
      }

      return {
        success: true,
        hash: receipt?.hash,
        blockNumber: receipt?.blockNumber,
        gasUsed: receipt?.gasUsed?.toString(),
        status: receipt?.status === 1 ? 'success' : 'failed'
      };
    } catch (error) {
      console.error('Error sending Ethereum:', error);
      return {
        success: false,
        error: error?.message || 'Transaction failed'
      };
    }
  }

  // Send Solana transaction
  async sendSolana(fromAddress, toAddress, amount) {
    try {
      const provider = window?.solana || window?.phantom?.solana;
      if (!provider) {
        throw new Error('Solana wallet not connected');
      }

      const fromPubkey = new PublicKey(fromAddress);
      const toPubkey = new PublicKey(toAddress);
      const lamports = new Decimal(amount)?.mul(LAMPORTS_PER_SOL)?.toNumber();

      const transaction = new Transaction()?.add(
        SystemProgram?.transfer({
          fromPubkey,
          toPubkey,
          lamports
        })
      );

      const { blockhash } = await this.solanaConnection?.getLatestBlockhash();
      transaction.recentBlockhash = blockhash;
      transaction.feePayer = fromPubkey;

      const signed = await provider?.signTransaction(transaction);
      const signature = await this.solanaConnection?.sendRawTransaction(signed?.serialize());
      await this.solanaConnection?.confirmTransaction(signature);

      // Save to database
      const { data: { user } } = await supabase?.auth?.getUser();
      if (user) {
        await this.saveTransaction(user?.id, null, {
          type: 'send',
          chain: 'solana',
          signature: signature,
          from: fromAddress,
          to: toAddress,
          asset: 'SOL',
          amount: amount,
          status: 'confirmed'
        });
      }

      return {
        success: true,
        signature,
        status: 'success'
      };
    } catch (error) {
      console.error('Error sending Solana:', error);
      return {
        success: false,
        error: error?.message || 'Transaction failed'
      };
    }
  }

  // Get transaction history for Ethereum
  async getEthereumTransactions(address, limit = 20) {
    try {
      if (!window?.ethereum) {
        return { transactions: [], error: 'MetaMask not installed' };
      }

      const provider = new BrowserProvider(window.ethereum);
      const currentBlock = await provider?.getBlockNumber();
      const transactions = [];

      // Scan recent blocks for transactions
      for (let i = 0; i < Math.min(limit, 10); i++) {
        const blockNumber = currentBlock - i;
        const block = await provider?.getBlock(blockNumber, true);

        if (block?.transactions) {
          for (const tx of block?.transactions?.slice(0, 5)) {
            if (typeof tx === 'object' && 
                (tx?.from?.toLowerCase() === address?.toLowerCase() || 
                 tx?.to?.toLowerCase() === address?.toLowerCase())) {
              
              transactions?.push({
                hash: tx?.hash,
                from: tx?.from,
                to: tx?.to,
                value: formatEther(tx?.value || 0),
                timestamp: block?.timestamp,
                blockNumber: tx?.blockNumber,
                type: tx?.from?.toLowerCase() === address?.toLowerCase() ? 'send' : 'receive',
                status: 'confirmed',
                chain: 'ethereum'
              });
            }
          }
        }

        if (transactions?.length >= limit) break;
      }

      return { transactions, error: null };
    } catch (error) {
      console.error('Error fetching Ethereum transactions:', error);
      return { transactions: [], error: error?.message };
    }
  }

  // Get transaction history for Solana
  async getSolanaTransactions(address, limit = 20) {
    try {
      const publicKey = new PublicKey(address);
      const signatures = await this.solanaConnection?.getSignaturesForAddress(
        publicKey,
        { limit }
      );

      const transactions = await Promise.all(
        signatures?.map(async (sig) => {
          const tx = await this.solanaConnection?.getParsedTransaction(sig?.signature);
          
          if (!tx) return null;

          const instruction = tx?.transaction?.message?.instructions?.[0];
          const type = instruction?.parsed?.type || 'unknown';

          return {
            signature: sig?.signature,
            timestamp: sig?.blockTime,
            type: type === 'transfer' ? 
              (instruction?.parsed?.info?.source === address ? 'send' : 'receive') : 
              type,
            status: sig?.err ? 'failed' : 'confirmed',
            chain: 'solana',
            slot: sig?.slot
          };
        })
      );

      return { 
        transactions: transactions?.filter(tx => tx !== null), 
        error: null 
      };
    } catch (error) {
      console.error('Error fetching Solana transactions:', error);
      return { transactions: [], error: error?.message };
    }
  }

  // Get combined transaction history (from database first, then blockchain)
  async getAllTransactions(wallets, limit = 20) {
    try {
      const { data: { user } } = await supabase?.auth?.getUser();
      if (!user) return [];

      // Get from database first
      const dbTransactions = await this.getUserTransactions(user?.id, limit);

      // If we have enough from database, return those
      if (dbTransactions?.length >= limit) {
        return dbTransactions?.map(tx => this.formatTransaction(tx));
      }

      // Otherwise, fetch from blockchain and merge
      const [ethTxs, solTxs] = await Promise.all([
        wallets?.ethereum ? this.getEthereumTransactions(wallets?.ethereum, limit) : { transactions: [] },
        wallets?.solana ? this.getSolanaTransactions(wallets?.solana, limit) : { transactions: [] }
      ]);

      const allTransactions = [
        ...dbTransactions?.map(tx => this.formatTransaction(tx)),
        ...ethTxs?.transactions?.map(tx => this.formatTransaction(tx)),
        ...solTxs?.transactions?.map(tx => this.formatTransaction(tx))
      ]?.sort((a, b) => (b?.timestamp || 0) - (a?.timestamp || 0));

      return allTransactions?.slice(0, limit);
    } catch (error) {
      console.error('Error fetching all transactions:', error);
      return [];
    }
  }

  // Estimate transaction fee
  async estimateEthereumFee(toAddress, amount) {
    try {
      const provider = new BrowserProvider(window.ethereum);
      const signer = await provider?.getSigner();
      
      const gasEstimate = await provider?.estimateGas({
        to: toAddress,
        value: parseEther(amount?.toString())
      });

      const feeData = await provider?.getFeeData();
      const gasCost = gasEstimate * (feeData?.gasPrice || 0n);

      return {
        gasLimit: gasEstimate?.toString(),
        gasPrice: feeData?.gasPrice?.toString(),
        totalFee: formatEther(gasCost),
        currency: 'ETH'
      };
    } catch (error) {
      console.error('Error estimating fee:', error);
      return null;
    }
  }

  // Format transaction for display
  formatTransaction(tx) {
    return {
      id: tx?.hash || tx?.signature || tx?.id,
      type: tx?.type || tx?.transaction_type,
      asset: tx?.asset || tx?.asset_symbol || (tx?.chain === 'ethereum' ? 'ETH' : 'SOL'),
      amount: tx?.value || tx?.amount || '0',
      from: tx?.from || tx?.from_address,
      to: tx?.to || tx?.to_address,
      timestamp: tx?.timestamp ? (typeof tx?.timestamp === 'number' ? new Date(tx.timestamp * 1000)?.toLocaleString() : new Date(tx.timestamp)?.toLocaleString()) : 'Unknown',
      status: tx?.status,
      chain: tx?.chain
    };
  }
}

export const transactionService = new TransactionService();
export default transactionService;
